/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CompanyAdd from './add';
import CompanyAction from '../../store/actions/CompanyAction';
import LoadingSkeleton from '../../utils/LoadingSkeleton';
import NoRowsOverlay from '../../utils/NoRowOverlay';
import CompanyView from './view';
import CompanyEdit from './edit';
import CompanyDelete from './delete';
import CopyInput from '../../components/CopyInput';
import useHasPermission from '../../utils/useHasPermission';
import { company, show, store, update, destroy } from '../../utils/constants';

function QuickSearchToolbar() {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <Box
      className="tableTopWrap page__title"
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3
      }}
    >
      <Stack direction="row" sx={{ ml: 'auto' }} spacing={2} className="MuiDataGrid-Search">
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) => searchInput.split(',').map((value) => value.trim())}
          quickFilterFormatter={(quickFilterValues) => quickFilterValues.join(', ')}
          debounceMs={800} // time before applying the new quick filter value
          variant="outlined"
          size="small"
          sx={{ pb: 0, maxWidth: '275px' }}
        />
        {useHasPermission(company, store) && (
          <Button
            className="button_primary"
            variant="contained"
            size="small"
            color="secondary"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add Company
          </Button>
        )}
      </Stack>
      {addOpen === true ? (
        <CompanyAdd openmodal={addOpen} onCloseModal={() => setAddOpen(false)} />
      ) : (
        ''
      )}
    </Box>
  );
}

export default function CompanyList() {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10
  });

  const [sortInfo, setSortInfo] = React.useState({
    sortField: '',
    sort: ''
  });

  const [searchKey, setSearchKey] = React.useState('');
  const [viewOpen, setViewOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [companyId, setCompanyId] = React.useState('');
  const [rowCountState, setRowCountState] = React.useState(0);
  const [data, setData] = React.useState([]);
  const dispatch = useDispatch();
  const { companyData, isLoading } = useSelector((state) => state.company); // Replace 'data' with your reducer name
  const { paginationData, sortingData, keyword } = useSelector(
    (state) => state.company.queryParams
  );

  useEffect(() => {
    dispatch(CompanyAction.updateQueryParams(paginationModel, sortInfo, searchKey));
    dispatch(CompanyAction.getAll(paginationModel, sortInfo, searchKey));
  }, [dispatch, paginationModel, sortInfo, searchKey]);

  useEffect(() => {
    if (paginationData) {
      setPaginationModel(paginationData);
    }
    if (sortingData) {
      setSortInfo(sortingData);
    }
    if (keyword) {
      setSearchKey(keyword);
    }
  }, [paginationData, keyword, sortingData]);

  useEffect(() => {
    if (companyData) {
      if (companyData.data) {
        setData(companyData.data);
      } else {
        setData([]);
      }
      setRowCountState((prevRowCountState) =>
        companyData.total !== undefined ? companyData.total : prevRowCountState
      );
    }
  }, [companyData]);

  const columns = [
    {
      field: 'unique_id',
      headerName: '',
      sortable: false,
      align: 'left',
      flex: 0.3,
      renderCell: (params) => {
        if (params && params.value) {
          return <CopyInput id={params.value} />;
        }
        return null;
      }
    },
    {
      field: 'name',
      headerName: 'Company Name',
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        if (params && params.value) {
          return params.value === 1 ? (
            <Chip label="Active" color="success" />
          ) : (
            <Chip label="Inactive" color="lignhtwarning" />
          );
        }
        return null;
      }
    },

    useHasPermission(company) && {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      getActions: (params) => [
        company && useHasPermission(company, show) ? (
          <GridActionsCellItem
            key={0}
            icon={<i className="icon-view" />}
            className="actionBtn"
            label="View"
            title="View"
            onClick={() => {
              setViewOpen(true);
              setCompanyId(params.id);
            }}
          />
        ) : (
          <span />
        ),
        company && useHasPermission(company, update) ? (
          <GridActionsCellItem
            key={0}
            icon={<i className="icon-edit" />}
            className="actionBtn"
            label="Edit"
            title="Edit"
            onClick={() => {
              setEditOpen(true);
              setCompanyId(params.id);
            }}
          />
        ) : (
          <span />
        ),
        company && useHasPermission(company, destroy) ? (
          <GridActionsCellItem
            key={1}
            icon={<i className="icon-delete" />}
            className="actionBtn"
            title="Delete"
            label="Delete"
            onClick={() => {
              setDeleteOpen(true);
              setCompanyId(params.id);
            }}
          />
        ) : (
          <span />
        )
      ]
    }
  ];

  const handleSortModelChange = React.useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const sortData = {
        sortField: field,
        sort
      };
      setSortInfo(sortData);
    }
    // else {
    //   dispatch(CompanyAction.getAll(paginationModel, sortInfo));
    // }
    // eslint-disable-next-line
  }, []);

  const onFilterChange = React.useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    const searchKeyword = filterModel.quickFilterValues[0];
    setPaginationModel({ page: 0, pageSize: 10 });
    setSearchKey(searchKeyword);
    // dispatch(CompanyAction.getAll(paginationModel, sortInfo, keyword));
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ mb: 3 }} className="cardWrapper">
      <div className="pageTitleWrap">
        {/* <Stack direction="row" spacing={1} alignItems="center"> */}
        <Typography className="" component="h2" variant="h2">
          Companies
        </Typography>
        {/* </Stack> */}
      </div>
      <Box sx={{ bgcolor: 'white' }} className="dataTable__wrap">
        <DataGrid
          rows={data}
          getRowId={(row) => row._id}
          rowCount={rowCountState}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          autoHeight
          sx={{ minHeight: 650, '--DataGrid-overlayHeight': '300px' }}
          disableColumnFilter
          rowHeight={50}
          slots={{
            toolbar: QuickSearchToolbar,
            loadingOverlay: LoadingSkeleton,
            noRowsOverlay: NoRowsOverlay
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          onFilterModelChange={onFilterChange}
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </Box>

      {editOpen === true ? (
        <CompanyEdit
          openmodal={editOpen}
          itemId={companyId}
          onCloseModal={() => setEditOpen(false)}
        />
      ) : (
        ''
      )}
      {viewOpen === true ? (
        <CompanyView
          openmodal={viewOpen}
          itemId={companyId}
          onCloseModal={() => setViewOpen(false)}
        />
      ) : (
        ''
      )}
      {deleteOpen === true ? (
        <CompanyDelete
          openmodal={deleteOpen}
          itemId={companyId}
          onCloseModal={() => setDeleteOpen(false)}
        />
      ) : (
        ''
      )}
    </Box>
  );
}
